import { Directive, Injector, Type } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { CustomErrorPropertyOptions, ErrorService, SupportedErrorType } from "ng/src/app/core/services/error.service";
import { CommandPaletteAction, CommandPaletteService } from 'ng/src/app/shared/command-palette/command-palette.service';
import { DestroyableComponent } from "ng/src/app/shared/destroyable.component";
import { FormInputDialogEntry } from "ng/src/app/shared/form-input-dialog/form-input-dialog.model";
import { CustomErrorProperties } from "ng/src/app/shared/model/error.model";
import { PrimeIcons } from "primeng/api";
import { DatePipe } from "@angular/common";
import { Utils } from "ng/src/app/core/utils";

@Directive()
export abstract class BaseComponent extends DestroyableComponent {
	
	public constructor(
		protected injector: Injector
	) {
		super();
	}
	
	private _datePipe?: DatePipe;
	private _errorService?: ErrorService;
	private _key?: string;
	
	public readonly Icons = PrimeIcons;
	public readonly Utils = Utils;
	
	get datePipe() {
		if (this._datePipe) return this._datePipe;
		return this._datePipe = this.getFromType(DatePipe);
	}
	
	get errorService() {
		if (this._errorService) return this._errorService;
		return this._errorService = this.injector.get(ErrorService);
	}
	
	// Unique key for this component
	get key() {
		return this._key = this._key ? this._key : (BaseComponent._lastKey += 1).toString();
	}
	
	// Create a unique key based on the given parts based on the key of this component
	keyedId(part: object | number | string, ...parts: (object | number | string)[]) {
		parts.splice(0, 0, part);
		const id = parts.map(o => o.toString().toLowerCase()).join('-');
		return `${id}-${this.key}`;
	}
	
	formatDate(value: Parameters<DatePipe['transform']>[0], format?: string) {
		return this.datePipe.transform(value, format ?? 'MM/dd/yyyy HH:mm') ?? '';
	}
	
	sendError<K extends keyof CustomErrorProperties>(error: SupportedErrorType, options?: CustomErrorPropertyOptions<K>) {
		this.errorService.handleStandardError<K>(error, options);
	}
	
	getControlErrorText(control: AbstractControl): string | null {
		if (control.pristine || control.valid) {
			return null;
		}
		
		const errors = control.errors;
		if (errors == null) {
			return null;
		}
		
		const errorKeys = Object.keys(errors);
		if (errorKeys.length == 0) {
			return null;
		}
		
		return errorKeys[0];
	}
	
	isValidNumber(o: any): o is number {
		return Utils.isValidNumber(o);
	}
	
	stringIsNullOrEmpty(str?: string): str is string {
		return (str?.trim()?.length ?? 0) === 0
	}
	
	typeOf(value: any) {
		return typeof value;
	}
	
	keyIn<T extends object>(value: T, key: keyof T | (string & {})): key is keyof T {
		return key as string in value;
	}
	
	isType<TType>(value: any, type: Type<TType>): TType | undefined {
		return value instanceof type ? value : undefined;
	}
	
	isTypePredicate<TType>(type: Type<TType>): (o: any) => o is TType {
		return (o: any): o is TType => {
			return o instanceof type;
		}
	}
	
	castAs<TType>(o: any, type: TType | Type<TType>): TType {
		return o;
	}
	
	/**
	 * Registers a command palette action that will be removed on destroy
	 * @param actions
	 */
	registerActions(...actions: CommandPaletteAction[]) {
		const registration = this.injector.get(CommandPaletteService)
			.register(...actions);
		
		this.addOnDestroy(() => registration.unregister());
		return registration;
	}
	
	createTemplateContext<T, O>($implicitValue: T): { $implicit: T }
	createTemplateContext<T, O>($implicitValue: T, details: O): { $implicit: T } & O
	createTemplateContext<T, O>($implicitValue: T, details?: O): { $implicit: T } & O | { $implicit: T } {
		return {
			$implicit: $implicitValue,
			...details && details
		}
	}
	
	consoleLog(...args: any[]) {
		console.log(...args);
	}
	
	private getFromType<T>(type: Type<T>) {
		return Injector.create({
			providers: [
				{ provide: type }
			],
			parent: this.injector
		}).get(type);
	}
	
	private static _lastKey = 0;
}